import type { BirdiePhase } from './boardPhase'

/**
 * The countdown ring around the golfer's avatar (B16).
 *
 * Owner, 2026-09-08: "the countdown etc. we show to the right with the past game
 * results could be removed, on crash / footfall we have the time shown in the
 * middle nicely … when game starts no need to show anything only during the
 * betting phase and countdown to when the next round starts when settled … very
 * sleek and integrated into the game, maybe part of the capsule with the golfer
 * details". So the shared right-rail pill is gated off for Birdie (`page.tsx`)
 * and the clock moves onto the capsule as a 3-px gold ring around the avatar.
 *
 * Pure and separate from the component for the same reason `boardPhase` is: the
 * rule "nothing during the clip" is a one-line assertion here and an unprovable
 * reading of JSX there. It takes `boardPhase`'s OWN outputs (`phase`, `drain`,
 * `secondsLeft`, `settledAtMs`) plus the settled window, so the ring and the arc
 * hairline can never disagree about how much of the betting window is left —
 * they are literally the same number.
 */

export type AvatarRingMode =
  /** Betting is open: gold, draining. */
  | 'betting'
  /** The last few seconds: amber, still draining. */
  | 'closing'
  /** Between rounds: the ring FILLS toward the next `race_ready`. */
  | 'next'
  /** The clip, and anything without a clock: no ring, no number. */
  | 'none'

export interface AvatarRingInput {
  phase: BirdiePhase
  /** `boardPhase.drain` — 1 = the whole betting window is left, 0 = closed. */
  drain: number
  /** `boardPhase.secondsLeft` — whole seconds, 1 Hz semantics. */
  secondsLeft: number
  /** `boardPhase.settledAtMs` — the wire time of `race_settled`. */
  settledAtMs?: number
  /** `game.settledWindow` — how long the gap to the next round is, seconds. */
  settledWindowSec: number
  nowMs: number
  /** `boardPhase.hardLocked` — a hard round past the board's lock: the tee-off has the clock. */
  hardLocked?: boolean
  /**
   * The between-rounds fill is computed this far ahead of `nowMs`, ms — the
   * CSS ease the stroke trails its value by (`boardPhase.ringLeadMs`), so the
   * drawn ring is FULL as the next round opens rather than cut off part-way
   * (owner 10-29).
   */
  leadMs?: number
}

export interface AvatarRingView {
  mode: AvatarRingMode
  /**
   * How much of the ring is PAINTED, 0..1.
   *
   * Betting drains 1 → 0 (the window running out); the between-rounds ring
   * fills 0 → 1 (the next round arriving). Two directions, one number, so the
   * component writes one `stroke-dashoffset` and never branches on the mode.
   */
  drain: number
  /** The number beside the avatar, or null when nothing is shown. */
  seconds: number | null
  /**
   * B40 — THE LAST SECONDS, as a beat the board can keep.
   *
   * The final three seconds of a betting window are the one moment a player has
   * to act on, and the board announced them with a colour. `urgentBeat` is the
   * whole second remaining — 3, 2 or 1 — and `undefined` at every other moment,
   * including the whole of the rest of the window and every phase that is not a
   * betting one.
   *
   * It is the NUMBER and not a boolean because the tick has to fire once per
   * second on a capsule that is mounted for the length of the round: the value
   * changing is what changes which rule matches, and that is the only way to
   * replay a CSS one-shot from a prop. The same mechanism the tiles' putt beats
   * use, for the same reason.
   *
   * Restraint is the reason it stops at three. A window that tightened for its
   * whole length would be a board that is always urgent, which is a board that
   * is never urgent.
   */
  urgentBeat?: number
}

/** How many seconds out the board starts tightening. */
export const BIRDIE_URGENT_FROM = 3

const clamp01 = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.max(0, Math.min(1, value))
}

/** Whole seconds, never negative, never NaN — this number is rendered. */
const wholeSeconds = (value: number): number =>
  Number.isFinite(value) && value > 0 ? Math.floor(value) : 0

const NOTHING: AvatarRingView = { mode: 'none', drain: 0, seconds: null }

/** The beat for a betting window with `seconds` left, or undefined outside the last three. */
function urgentBeatFor(seconds: number): number | undefined {
  if (seconds <= 0 || seconds > BIRDIE_URGENT_FROM) return undefined
  return seconds
}

/**
 * The ring's state right now.
 *
 * Three windows and one silence:
 *
 *  - `open` → `betting`, `closing` → `closing`. Both take `boardPhase.drain`
 *    verbatim: it is already ms-precise (`boardPhase` derives it from
 *    `race_ready`'s timestamp rather than from the 1 Hz `secondsLeft`), which is
 *    what lets the ring transition smoothly over each tick instead of stepping.
 *  - `locked` → NOTHING. The owner was explicit: "when game starts no need to
 *    show anything". A countdown over a putt that is already rolling is noise,
 *    and the arc hairline is empty in this phase for the same reason.
 *  - `settled` → `next`, counting to the next `race_ready`. This is the one
 *    window `boardPhase` does not already model as a fraction (its `drain` is 0
 *    from `locked` onward), so it is derived here from `settledAtMs` and the
 *    game's settled window. The seconds come from `boardPhase.secondsLeft`,
 *    which `getRaceCountdownInfo` already floors against the same window — one
 *    clock, two readouts, so the number can never disagree with the ring.
 *  - `idle` → nothing: there is no round to count.
 *
 * A settled round with no `race_settled` timestamp (a hydrated race whose event
 * list is missing it, or a clock that has not caught up) shows the ring EMPTY
 * rather than full: an unknown gap must not read as "the next round is here".
 */
export function avatarRing(input: AvatarRingInput): AvatarRingView {
  if (input.hardLocked === true) return NOTHING
  if (input.phase === 'open' || input.phase === 'closing') {
    const seconds = wholeSeconds(input.secondsLeft)
    const beat = urgentBeatFor(seconds)
    return {
      mode: input.phase === 'closing' ? 'closing' : 'betting',
      drain: clamp01(input.drain),
      seconds,
      ...(beat === undefined ? {} : { urgentBeat: beat })
    }
  }

  if (input.phase === 'settled') {
    const { settledAtMs, settledWindowSec, nowMs } = input
    if (settledAtMs === undefined || Number.isNaN(settledAtMs) || settledWindowSec <= 0) {
      return { mode: 'next', drain: 0, seconds: wholeSeconds(input.secondsLeft) }
    }
    const elapsedMs = nowMs + (input.leadMs ?? 0) - settledAtMs
    return {
      mode: 'next',
      drain: clamp01(elapsedMs / (settledWindowSec * 1000)),
      seconds: wholeSeconds(input.secondsLeft)
    }
  }

  return NOTHING
}
